import type { Due, Item } from "../lib/api.js";
import { el } from "../lib/dom.js";
import { dueFromInputs, formatDue } from "../lib/format.js";
import type { Repeat } from "../lib/recurrence.js";
import { EMPTY_ITEM, field } from "./ui.js";

/**
 * An item's fields, built once for the Add popup and the Edit panel.
 *
 * The two forms ask for the same things and used to build them twice, which
 * is how they drifted: Add cleared the time on `input` and Edit only on
 * `change`, and the two blank-Item refusals were copies of each other. The
 * controls, their labels, their limits and the rules between them live here,
 * and each form arranges them its own way — Add leads with the item, Edit
 * shows everything the item holds.
 *
 * `prefix` keeps each form's ids (`add-item`, `detail-note`), which labels,
 * error descriptions, tests and the browser drivers all name.
 */

export type FieldPrefix = "add" | "detail";

/** The id of the page's one `<datalist>` of categories (g79), which every Category field names. */
export const CATEGORY_SUGGESTIONS = "category-suggestions";

/** What the form holds, trimmed, in the shapes the store takes. */
export interface ItemValues {
  text: string;
  quantity: string | null;
  category: string | null;
  note: string | null;
  due: Due | null;
  repeat: Repeat | null;
}

/** Exported so the tests pin the words, as `EMPTY_ITEM` is. */
export const PARTIAL_DATE = "Finish the date, or clear it.";
export const PARTIAL_TIME = "Finish the time, or clear it.";

/** What the note and schedule fields hold, in words, and what is wrong with them. */
export interface Held {
  values: string[];
  problems: string[];
}

export interface ItemFields {
  item: HTMLInputElement;
  quantity: HTMLInputElement;
  category: HTMLInputElement;
  note: HTMLTextAreaElement;
  date: HTMLInputElement;
  time: HTMLInputElement;
  repeat: HTMLSelectElement;
  /** Each control with its visible label, ready to place. */
  rows: {
    item: HTMLElement;
    quantity: HTMLElement;
    category: HTMLElement;
    note: HTMLElement;
    /** Due date and time side by side. */
    due: HTMLElement;
    repeat: HTMLElement;
  };
  read(): ItemValues;
  /**
   * Marks every field the form cannot take, says the first one aloud and
   * returns it for the form to bring into view and focus. Undefined when the
   * form can be saved.
   */
  validate(): HTMLElement | undefined;
  /** True when anything differs from what the form started with. */
  typed(): boolean;
  /** True while a date or time is only partly typed: a change `read` cannot see. */
  unfinished(): boolean;
  /**
   * The note and schedule, described for a line that stands in for them
   * while they are folded away: a value hidden and still saved must be seen.
   */
  held(): Held;
  /** Takes every field back to empty, as Add another does. */
  reset(): void;
  clearErrors(): void;
}

const REPEATS = ["daily", "weekly", "monthly", "yearly"] as const;

export function selectedRepeat(select: HTMLSelectElement): Repeat | null {
  return select.value ? (select.value as Repeat) : null;
}

/**
 * A date or time the browser is still holding half of. A native date control
 * with one segment typed reports `value === ""`, which reads exactly like a
 * cleared field, and saving that would quietly drop the schedule. Only the
 * validity says otherwise.
 */
const partial = (input: HTMLInputElement): boolean => input.validity.badInput;

/**
 * A finger on a date or time field asks the browser for its picker (g85). On
 * Andre's Android phone a tap opened nothing, in the installed app and in a
 * tab, and the cause was not found: touch emulation on a desktop opens the
 * field normally. Touch and pen only, so a mouse click still lands in the
 * segment it pointed at for typing. A browser without `showPicker`, or one
 * that refuses the call, is left to open the field its own way.
 */
function openPickerOnTap(control: HTMLInputElement): void {
  control.addEventListener("click", (event) => {
    const pointer = (event as PointerEvent).pointerType;
    if ((pointer !== "touch" && pointer !== "pen") || control.disabled) return;
    try {
      control.showPicker?.();
    } catch {
      // Already open, or not allowed here: the tap keeps its default.
    }
  });
}

/**
 * A time and a repeat both hang off a date: with no date there is no time the
 * server will take and no first occurrence to repeat from. So clearing the
 * date clears them, and they stay unavailable until there is one — a control
 * that silently discards what was typed into it is worse than one that is
 * plainly unavailable. Both events, because a picker reports on `change` and
 * typing on `input`, and the rule is the same either way.
 *
 * A date that is only partly typed is not a cleared one, so it leaves the
 * time and repeat alone.
 */
function bindSchedule(date: HTMLInputElement, time: HTMLInputElement, repeat: HTMLSelectElement): void {
  const update = (): void => {
    const empty = !date.value && !partial(date);
    time.disabled = empty;
    repeat.disabled = empty;
    if (empty) {
      time.value = "";
      repeat.value = "";
    }
  };
  date.addEventListener("input", update);
  date.addEventListener("change", update);
  update();
}

export function itemFields(
  prefix: FieldPrefix,
  initial: Partial<Pick<Item, "text" | "quantity" | "category" | "note" | "due" | "repeat">>,
  announce: (message: string) => void,
): ItemFields {
  const id = (name: string): string => `${prefix}-${name}`;
  const errorFor = (name: string): HTMLSpanElement =>
    el("span", { class: "field-error", attrs: { id: id(`${name}-error`), hidden: true } });

  const item = el("input", {
    attrs: { value: initial.text ?? "", maxlength: 1000, enterkeyhint: "done", "aria-describedby": id("item-error") },
  });
  const quantity = el("input", { attrs: { value: initial.quantity ?? "", maxlength: 50 } });
  // Suggested from the page's one list of categories (g79). Not the browser's
  // own form history, which knows nothing of scopes and would offer an
  // account's values on a shared list's page.
  const category = el("input", { attrs: { value: initial.category ?? "", maxlength: 64,
    list: CATEGORY_SUGGESTIONS, autocomplete: "off" } });
  const note = el("textarea", { attrs: { maxlength: 10_000, rows: 2 } });
  note.value = initial.note ?? "";

  const date = el("input", { attrs: { type: "date", value: initial.due?.date ?? "", "aria-describedby": id("date-error") } });
  const time = el("input", { attrs: { type: "time", value: initial.due?.time ?? "", "aria-describedby": id("time-error") } });
  const repeat = el("select", {
    children: [
      el("option", { text: "Does not repeat", attrs: { value: "" } }),
      ...REPEATS.map((r) => el("option", { text: r[0]!.toUpperCase() + r.slice(1), attrs: { value: r } })),
    ],
  });
  repeat.value = initial.repeat ?? "";
  bindSchedule(date, time, repeat);
  openPickerOnTap(date);
  openPickerOnTap(time);

  // The values the form opened with: Add's are empty, Edit's the item's.
  const snapshot = (): string[] => [item.value, quantity.value, category.value, note.value, date.value, time.value, repeat.value];
  const start = snapshot();

  /** Each checked control, the error beside it and what it says when refused. */
  const checks: { control: HTMLInputElement; error: HTMLSpanElement; refused(): string | undefined }[] = [
    { control: item, error: errorFor("item"), refused: () => (item.value.trim() ? undefined : EMPTY_ITEM) },
    { control: date, error: errorFor("date"), refused: () => (partial(date) ? PARTIAL_DATE : undefined) },
    { control: time, error: errorFor("time"), refused: () => (!time.disabled && partial(time) ? PARTIAL_TIME : undefined) },
  ];
  const clear = ({ control, error }: (typeof checks)[number]): void => {
    error.textContent = "";
    error.hidden = true;
    control.removeAttribute("aria-invalid");
  };
  for (const check of checks) {
    check.control.addEventListener("input", () => clear(check));
    check.control.addEventListener("change", () => clear(check));
  }
  const [itemCheck, dateCheck, timeCheck] = checks as [(typeof checks)[number], (typeof checks)[number], (typeof checks)[number]];

  return {
    item,
    quantity,
    category,
    note,
    date,
    time,
    repeat,
    rows: {
      item: field(id("item"), "Item", item, itemCheck.error),
      quantity: field(id("quantity"), "Quantity", quantity),
      category: field(id("category"), "Category", category),
      note: field(id("note"), "Note", note),
      due: el("div", {
        class: "due-row",
        children: [field(id("date"), "Due date", date, dateCheck.error), field(id("time"), "Due time", time, timeCheck.error)],
      }),
      repeat: field(id("repeat"), "Repeat", repeat),
    },
    read: () => ({
      text: item.value.trim(),
      quantity: quantity.value.trim() || null,
      category: category.value.trim() || null,
      note: note.value.trim() || null,
      due: dueFromInputs(date.value, time.value),
      repeat: selectedRepeat(repeat),
    }),
    validate: () => {
      let first: { control: HTMLElement; message: string } | undefined;
      for (const check of checks) {
        const message = check.refused();
        if (!message) {
          clear(check);
          continue;
        }
        check.error.textContent = message;
        check.error.hidden = false;
        check.control.setAttribute("aria-invalid", "true");
        first ??= { control: check.control, message };
      }
      // Said as well as shown: a description that changes under a focused
      // field is announced by nothing.
      if (first) announce(first.message);
      return first?.control;
    },
    typed: () => partial(date) || partial(time) || snapshot().some((value, at) => value !== start[at]),
    unfinished: () => partial(date) || partial(time),
    held: () => {
      const due = dueFromInputs(date.value, time.value);
      const every = selectedRepeat(repeat);
      return {
        values: [
          ...(note.value.trim() ? ["Note"] : []),
          ...(due ? [formatDue(due)] : []),
          ...(every ? [`Repeats ${every}`] : []),
        ],
        problems: [
          ...(partial(date) || date.getAttribute("aria-invalid") ? ["Due date needs fixing"] : []),
          ...(partial(time) || time.getAttribute("aria-invalid") ? ["Due time needs fixing"] : []),
        ],
      };
    },
    reset: () => {
      for (const control of [item, quantity, category, note, date, time]) control.value = "";
      date.dispatchEvent(new Event("change"));
      for (const check of checks) clear(check);
    },
    clearErrors: () => checks.forEach(clear),
  };
}
